import React, { useMemo, useRef, useState } from "react";
import { Button, Modal, Spinner, Table } from "react-bootstrap";
import StudentPhotoUploadService, {
  CommitResponse,
  CommitResult,
  PreviewEntry,
  PreviewResponse,
  PreviewStatus,
} from "../../_services/StudentPhotoUploadService";
import "./student-photo-upload.css";

type FilterMode = "all" | "matched" | "errors";

const StudentPhotoUploadPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [commitResult, setCommitResult] = useState<CommitResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [filter, setFilter] = useState<FilterMode>("all");

  const reset = () => {
    setFile(null);
    setFileError(null);
    setPreview(null);
    setCommitResult(null);
    setFilter("all");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const acceptFile = (f: File) => {
    if (!f.name.toLowerCase().endsWith(".zip")) {
      setFileError("Please choose a .zip file");
      return;
    }
    setFileError(null);
    setFile(f);
    setPreview(null);
    setCommitResult(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) acceptFile(f);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) acceptFile(f);
  };

  const handlePreview = async () => {
    if (!file || previewing) return;
    setPreviewing(true);
    setPreview(null);
    setCommitResult(null);
    try {
      const data = await StudentPhotoUploadService.preview(file);
      setPreview(data);
      if (data.status !== 1) {
        setFileError(data.message || "Preview failed");
      }
    } catch (err: any) {
      setFileError(err?.response?.data?.message || err?.message || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const handleCommit = async () => {
    if (!file || uploading) return;
    setConfirmOpen(false);
    setUploading(true);
    setCommitResult(null);
    try {
      const data = await StudentPhotoUploadService.commit(file);
      setCommitResult(data);
      if (data.status !== 1) {
        setFileError(data.message || "Upload failed");
      }
    } catch (err: any) {
      setFileError(err?.response?.data?.message || err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const previewSummary = preview?.summary;
  const previewEntries: PreviewEntry[] = preview?.entries || [];
  const commitSummary = commitResult?.summary;
  const commitRows: CommitResult[] = commitResult?.results || [];

  const filteredPreview = useMemo(() => {
    if (filter === "matched") return previewEntries.filter((e) => e.status === "matched");
    if (filter === "errors") return previewEntries.filter((e) => e.status !== "matched");
    return previewEntries;
  }, [previewEntries, filter]);

  const matchedCount = previewSummary?.matched || 0;
  const errorCount = previewEntries.length - matchedCount;
  const canCommit = matchedCount > 0 && !uploading && !commitResult;

  return (
    <div className="student-photo-upload">
      <div className="page-header">
        <h2>Bulk Student Photo Upload</h2>
        <div className="subtitle">
          Upload a ZIP of student photos. Files are matched to students by file name = Student Code.
        </div>
      </div>

      <div className="instructions">
        <strong>How it works:</strong>
        <ul>
          <li>Prepare a ZIP containing image files (.jpg, .jpeg, .png).</li>
          <li>Each file name (without extension) must match the student's Student Code. Example: <code>STU-2024-017.jpg</code>.</li>
          <li>If a Student Code contains a slash like <code>21/FB/03</code>, rename the file to <code>21_FB_03.jpg</code>.</li>
          <li>Click <em>Preview</em> to see which files match before any data is changed. Then click <em>Upload Matched Photos</em> to save.</li>
        </ul>
      </div>

      {!commitResult && (
        <div
          className={`drop-zone ${dragActive ? "drag-active" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <div className="icon">📦</div>
          <div>
            <strong>Drop a .zip file here</strong> or click to choose
          </div>
          {file && <div className="filename">📁 {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</div>}
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
        </div>
      )}

      {fileError && (
        <div className="alert alert-danger mt-3" role="alert">
          {fileError}
        </div>
      )}

      <div className="action-bar">
        {!commitResult && (
          <>
            <Button variant="secondary" onClick={reset} disabled={previewing || uploading}>
              Clear
            </Button>
            <Button variant="primary" onClick={handlePreview} disabled={!file || previewing || uploading}>
              {previewing ? <><Spinner size="sm" animation="border" /> Previewing…</> : "Preview"}
            </Button>
            <Button
              variant="success"
              onClick={() => setConfirmOpen(true)}
              disabled={!canCommit}
              title={matchedCount === 0 ? "Run preview first; at least one matched file is required" : undefined}
            >
              {uploading ? <><Spinner size="sm" animation="border" /> Uploading…</> : `Upload Matched Photos (${matchedCount})`}
            </Button>
          </>
        )}
        {commitResult && (
          <Button variant="primary" onClick={reset}>
            Upload Another ZIP
          </Button>
        )}
      </div>

      {previewSummary && !commitResult && (
        <>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="label">Total Files</div>
              <div className="value">{previewSummary.total}</div>
            </div>
            <div className="summary-card matched">
              <div className="label">Matched</div>
              <div className="value">{previewSummary.matched}</div>
            </div>
            <div className="summary-card error">
              <div className="label">Not Found</div>
              <div className="value">{previewSummary.notFound}</div>
            </div>
            <div className="summary-card warning">
              <div className="label">Invalid Type</div>
              <div className="value">{previewSummary.invalidType}</div>
            </div>
            <div className="summary-card warning">
              <div className="label">Duplicate Code</div>
              <div className="value">{previewSummary.duplicateCode}</div>
            </div>
          </div>

          <div className="filter-tabs">
            <Button size="sm" variant={filter === "all" ? "primary" : "secondary"} onClick={() => setFilter("all")}>
              All ({previewEntries.length})
            </Button>
            <Button size="sm" variant={filter === "matched" ? "primary" : "secondary"} onClick={() => setFilter("matched")}>
              Matched ({matchedCount})
            </Button>
            <Button size="sm" variant={filter === "errors" ? "primary" : "secondary"} onClick={() => setFilter("errors")}>
              Errors ({errorCount})
            </Button>
          </div>

          <div className="results-table">
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Student Code</th>
                  <th>Student</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredPreview.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">No rows for this filter</td>
                  </tr>
                )}
                {filteredPreview.map((row, idx) => (
                  <tr key={`${row.fileName}-${idx}`}>
                    <td>{row.fileName}</td>
                    <td>{row.studentCode}</td>
                    <td>{row.studentName || "—"}</td>
                    <td>
                      <span className={`status-pill ${row.status}`}>
                        {labelFor(row.status)}
                      </span>
                    </td>
                    <td className="text-muted">{row.message || ""}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}

      {commitSummary && (
        <>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="label">Total</div>
              <div className="value">{commitSummary.total}</div>
            </div>
            <div className="summary-card uploaded">
              <div className="label">Uploaded</div>
              <div className="value">{commitSummary.uploaded}</div>
            </div>
            <div className="summary-card skipped">
              <div className="label">Skipped</div>
              <div className="value">{commitSummary.skipped}</div>
            </div>
            <div className="summary-card failed">
              <div className="label">Failed</div>
              <div className="value">{commitSummary.failed}</div>
            </div>
          </div>

          <div className="results-table">
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Student Code</th>
                  <th>Student</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {commitRows.map((row, idx) => (
                  <tr key={`${row.fileName}-${idx}`}>
                    <td>{row.fileName}</td>
                    <td>{row.studentCode}</td>
                    <td>{row.studentName || "—"}</td>
                    <td>
                      <span className={`status-pill ${row.status}`}>{labelFor(row.status)}</span>
                    </td>
                    <td className="text-muted">{row.message || ""}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <Modal show={confirmOpen} onHide={() => setConfirmOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Upload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You are about to upload <strong>{matchedCount}</strong> matched photo{matchedCount === 1 ? "" : "s"}.
          {errorCount > 0 && (
            <>
              {" "}<span className="text-muted">{errorCount} unmatched file{errorCount === 1 ? "" : "s"} will be skipped.</span>
            </>
          )}
          {" "}This will overwrite any existing staff-uploaded photo for these students. Continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="success" onClick={handleCommit}>Upload {matchedCount} Photo{matchedCount === 1 ? "" : "s"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

function labelFor(status: PreviewStatus | CommitResult["status"]): string {
  switch (status) {
    case "matched": return "Matched";
    case "not-found": return "No match";
    case "invalid-type": return "Invalid type";
    case "duplicate-code": return "Duplicate code";
    case "uploaded": return "Uploaded";
    case "skipped": return "Skipped";
    case "failed": return "Failed";
    default: return status;
  }
}

export default StudentPhotoUploadPage;
